import { useForm } from "react-hook-form";
import { useState, useEffect, useRef, useMemo } from "react";
import Modal from "./Modal";
import ModalRemove from "./ModalRemove";
import { useDispatch, useSelector } from "react-redux";
import {
  ObtenerTiposFinanciamientos,
  ActualizarTipoFinanciamiento,
  EliminarTipoFinanciamiento,
  InsertarTipoFinanciamiento,
} from "../../redux/tiposFinanciamientosSlice";
import WindowDimension from "../../Hooks/WindowDimension";
import useSubmit from "../../Hooks/useSubmit";
import { OrderTable } from "../../Components/OrderTable";
import { getAdminFinancialTypePage } from "../../Api/content";
import { useLocale } from "../../i18n/localeContext";
import { Skeleton } from "antd";
import { StrapiError } from '../../Inicio/Pages/StrapiError';
import { _translateValue } from "../translation-gestor/translateValue";

import { useValidation } from "../../Hooks/useValidation";

const SCREEN_NAME = "PantallaTiposFinanciamientos";
function TiposFinanciamientos(props) {
  const { validation } = useValidation();
  const { locale } = useLocale();
  const [adminFinancialTypePage, setAdminFinancialTypePage] = useState(null);
  const [isLoadingContent, setIsLoadingContent] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [translatedNombres, setTranslatedNombres] = useState({});
  const [isTranslating, setIsTranslating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [tituloModal, setTituloModal] = useState("Agregar");
  const [removeState, changeRemoveState] = useState(false);
  const [itemSelected, setItemSelected] = useState({});
  const { width } = WindowDimension();

  const { submitAction } = useSubmit();

  const dispatch = useDispatch();
  let busqueda = useRef();

  const { tiposFinanciamientos } = useSelector(
    (state) => state.tiposFinanciamientos
  );

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    setValue,
  } = useForm();

  const columns = useMemo(() => {
    const table = adminFinancialTypePage?.financialtype_table  ?? [];
    return [
      { label: table[0]?.label || "Código", accessor: "tipoFinanciamientoEstudioId", sortable: true },
      { 
        label: table[1]?.label || "Nombre", 
        accessor: "tipoFinanciamientoEstudioNombre", 
        sortable: true,
        render: (item) => (
          <td>{translatedNombres[item.tipoFinanciamientoEstudioNombre] || item.tipoFinanciamientoEstudioNombre}</td>
        ),
      },
      { label: table[2]?.label || "Acción", accessor: "action", sortable: false },
    ];
  }, [adminFinancialTypePage, translatedNombres]);

  const showModalEdit = (item) => {
    reset(item);
    setTituloModal(adminFinancialTypePage?.subtitleO || "Editar tipo de financiamiento");
    setShowModal(true);
  };

  const closeModalEdit = () => {
    reset({
      tipoFinanciamientoEstudioId: 0,
      tipoFinanciamientoId: 0,
      tipoFinanciamientoEstudioNombre: "",
    });
    setShowModal(false);
  };

  const showRemoveModalEdit = (item) => {
    setItemSelected(item);
    changeRemoveState(true);
  };

  const closeRemoveModalEdit = () => {
    setItemSelected({});
    changeRemoveState(false);
  };

  const cargarFinanciamientos = () => {
    dispatch(ObtenerTiposFinanciamientos());
  };

  const cargarFinanciamientoPorCaracter = () => {
    dispatch(ObtenerTiposFinanciamientos(busqueda.current.value));
  };

  const eliminar = async () => {
    submitAction(
      EliminarTipoFinanciamiento,
      itemSelected.tipoFinanciamientoEstudioId,
      () => {
        closeRemoveModalEdit();
        cargarFinanciamientos();
      }
    );
  };

  const onSubmit = async (data) => {
    submitAction(
      data.tipoFinanciamientoEstudioId > 0
        ? ActualizarTipoFinanciamiento
        : InsertarTipoFinanciamiento,
      data,
      () => {
        closeModalEdit();
        cargarFinanciamientos();
      }
    );
  };

  useEffect(() => {
    cargarFinanciamientos();
  }, [dispatch]);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    // Timeout para evitar carga eterna
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setAdminFinancialTypePage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 5000);
    
    getAdminFinancialTypePage(locale)
      .then((res) => {
        clearTimeout(timeoutId);
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          setAdminFinancialTypePage(data);
          setIsLoadingContent(false);
        }
      })
      .catch((error) => { 
        clearTimeout(timeoutId);
        if (!cancel) {
          setAdminFinancialTypePage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => { 
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale])

  // Efecto para traducir el campo "Nombre" cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasTiposFinanciamientos = tiposFinanciamientos && Array.isArray(tiposFinanciamientos) && tiposFinanciamientos.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasTiposFinanciamientos) {
        setIsTranslating(false);
        return;
      }

      // Iniciar traducción
      setIsTranslating(true);

      const nombreTranslations = {};

      // Traducir solo el campo "Nombre" (filtrar los que no sean tipoFinanciamientoEstudioId == 3)
      const filteredData = tiposFinanciamientos.filter((a) => a.tipoFinanciamientoEstudioId != 3);
      const translationPromises = filteredData.map(async (item) => {
        if (item.tipoFinanciamientoEstudioNombre) {
          const translated = await _translateValue(SCREEN_NAME, item.tipoFinanciamientoEstudioNombre, locale);
          nombreTranslations[item.tipoFinanciamientoEstudioNombre] = translated;
        }
      });
      await Promise.all(translationPromises);

      // Actualizar estados y finalizar traducción
      setTranslatedNombres(nombreTranslations);
      setIsTranslating(false);
    };

    translateData();
  }, [tiposFinanciamientos, locale]);

  if (isLoadingContent || isTranslating) {
    return (
      <div className="main-admin">
        <div className="main-admin-bar">
          <div className="header-admin-bar-items">
            <Skeleton.Button style={{ width: 40, height: 40 }} active />
            <Skeleton.Input style={{ width: 300, height: 30 }} active />
          </div>
          <div className="header-admin-bar-items ml-auto">
            <Skeleton.Input style={{ width: 200, height: 40 }} active />
          </div>
        </div>
        <div className="table-container">
          <Skeleton.Input style={{ width: '100%', height: 400, minWidth: '800px' }} active />
        </div>
      </div>
    );
  }

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setAdminFinancialTypePage(null);
      }}
    />
  }

  return (
    <div className="main-admin">
      <div className="main-admin-bar">
        <div className="header-admin-bar-items">
          <button
            className="main-admin-button"
            onClick={() => {
              setShowModal(true);
              setTituloModal(adminFinancialTypePage?.subtitleTR || "Agregar");
              setValue("tipoFinanciamientoEstudioNombre", "");
              reset();
            }}
          >
            +
          </button>
          <h2>{adminFinancialTypePage?.title || "Tipos de financiamientos"}</h2>
        </div>
        <div className="header-admin-bar-items ml-auto">
          <label hidden>Búsqueda</label>
          <input
            type="search"
            className="form-control"
            onInput={cargarFinanciamientoPorCaracter}
            placeholder={adminFinancialTypePage?.search_input || "Búsqueda"}
            ref={busqueda}
          />
        </div>
      </div>

      <div className="table-container">
        {tiposFinanciamientos.length > 0 ? (
          <OrderTable
          {...{ columns }}
          data={tiposFinanciamientos
            ?.filter((a) => a.tipoFinanciamientoEstudioId != 3)}
          clase="table-adm"
          actions={[
            {
              icon: "",
              clase: "text-primary",
              style: { display: "contents" },
              event: (item) => showModalEdit(item),
            },
            {
              icon: "",
              clase: "delete",
              style: { display: "contents" },
              event: (item) => showRemoveModalEdit(item),
            },
          ]}
        />
        ) : (
          <div className="no-results-container">
            <img src={props.img} alt="Imagen sin resultados" />
            <h1>{props.noResultMessage}</h1>
          </div>
        )}
      </div>

      <Modal
        estado={showModal}
        cambiarEstado={closeModalEdit}
        titulo={tituloModal}
        clase={`${width < 576 ? "full-width" : "small"}`}
        buttonGroup={
          <>
            <button
              type="submit"
              className="button-text"
              onClick={handleSubmit(onSubmit)}
            >
              {adminFinancialTypePage?.save_button || "Guardar"}
            </button>
          </>
        }
      >
        <form>
          <div className="form-group">
            <i className="material-icons important"></i>
            <label>{adminFinancialTypePage?.financialtype || "Tipo de financiamiento"}</label>
            <input
              className="form-control"
              name="tipoFinanciamientoEstudioNombre"
              {...register("tipoFinanciamientoEstudioNombre", {
                required: validation?.required_field || "Campo requerido",
              })}
            />
            {errors.tipoFinanciamientoEstudioNombre && (
              <p className="error">
                {errors.tipoFinanciamientoEstudioNombre.message}
              </p>
            )}
          </div>
        </form>
      </Modal>

      <ModalRemove
        estado={removeState}
        closeModalHandled={closeRemoveModalEdit}
        title={adminFinancialTypePage?.subtitleT || "Eliminar plantillla"}
        message={adminFinancialTypePage?.question || "¿Seguro que desea eliminar el siguiente registro?"}
        item={itemSelected.tipoFinanciamientoEstudioNombre}
        btnGroup={
          <>
            <button className="button-text text-red" onClick={eliminar}>
              {adminFinancialTypePage?.confirm_button || "Confirmar"}
            </button>
            <button
              className="button-text text-grey"
              onClick={() => {
                closeRemoveModalEdit(false);
              }}
            >
              {adminFinancialTypePage?.cancel_button || "Cancelar"}
            </button>
          </>
        }
      />
    </div>
  );
}

export default TiposFinanciamientos;
